import {ConfigObjectType, LightSplitLayout, LoadingSpinner, useApiQuery, useLocationQuery} from '@wallaby-ui/core';
import {
  DocumentProp,
  DynamicItemDefinition,
  EncounterProp,
  HaidiSchema,
  PatientEncounterDocumentBreadcrumbs,
  PatientProp,
  documentContainer,
  formatDocumentBreadcrumb,
  formatEncounterBreadcrumb,
  formatPatientBreadcrumb,
  getDocumentTimestamp,
  useAddDynamicItem,
  useFormatters,
} from '@wallaby-ui/document';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import {defineMessages} from 'react-intl';
import {useParams} from 'react-router-dom';

import {EncounterDocumentLayout} from './EncounterDocumentLayout';
import {EncounterLayout} from './EncounterLayout';
import {EncounterLayoutTabs} from './EncounterLayoutTabs';
import stylesLayout from '../AppLayout.less';

const I18N = defineMessages({
  LOADING: {
    id: 'document.loading',
    defaultMessage: 'Loading ...',
    description: 'text for loading spinner in document (and others) box',
  },
});

interface OwnProps {
  config: ConfigObjectType;
}

type Props = OwnProps;

export const EncounterSharedLayout: React.FC<Props> = (props) => {
  const {config} = props;

  let {encounterId, documentId} = useParams();
  encounterId = encounterId ?? '';
  documentId = documentId ?? '';

  const storageIdPrefix = encounterId ? `/encounter/${encounterId}` : `/document/${documentId}`;

  const encounterPropertiesUrl = config.service.url.encounterProperties.replace('{encounterId}', encounterId);
  const {data: encounterData, isLoading: encounterIsLoading} = useApiQuery<HaidiSchema.components['schemas']['EncounterPropertiesDto']>(
    [encounterPropertiesUrl],
    null,
    {
      axiosConfig: {
        url: encounterPropertiesUrl,
      },
      reactQueryOptions: {
        enabled: Boolean(encounterId),
      },
    }
  );

  const {rawDocumentData: _document, isLoading: documentIsLoading} = documentContainer.useLoadDocument({
    documentId,
    encounterId,
    config,
    isHaidi: true,
  });
  const document = _document as HaidiSchema.components['schemas']['DocumentViewDto'];

  const breadcrumbProps: {
    patient: PatientProp;
    encounter: EncounterProp;
    document: DocumentProp;
  } = useMemo(() => {
    const patientProp: PatientProp = {
      isLoading: encounterIsLoading && documentIsLoading, // NOTE: we only need one
      id: encounterData?.patientId ?? document?.patientProperties?.patientId,
      name: encounterData?.fullName ?? document?.patientProperties?.patientFullName,
      birthNumber: encounterData?.identificationNumber ?? document?.patientProperties?.patientIdentificationNumber,
    };
    const encounterProp: EncounterProp = {
      isLoading: encounterIsLoading,
      id: encounterId,
      encounterType: encounterData?.encounterType,
      orgShortName: encounterData?.admissionDepartment,
      startDate: encounterData?.encounterStartedAt,
      endDate: encounterData?.encounterEndedAt,
      isDeleted: encounterData?.encounterIsDeleted,
      isEscort: encounterData?.encounterIsEscort,
    };
    const documentProp: DocumentProp = {
      isLoading: documentIsLoading,
      id: documentId,
      name: document?.documentProperties.documentName,
      timestamp: getDocumentTimestamp(document),
    };
    return {
      patient: patientProp,
      encounter: encounterProp,
      document: documentProp,
    };
  }, [encounterIsLoading, documentIsLoading, encounterId, encounterData, documentId, document]);

  const formatters = useFormatters();
  const {addDynamicItem} = useAddDynamicItem();
  const [query, changeQuery] = useLocationQuery(storageIdPrefix, true, () => '', ['docPaging']);
  React.useEffect(() => {
    if (!(encounterId || documentId)) return;
    const encounterType = encounterData?.encounterType || 'HOSPITALIZATION';
    let caseType: 'HOSPITALIZATION' | 'OUTPATIENT' | 'DOCUMENT' | 'DOCUMENT_OUTSIDE_ENCOUNTER';
    if (documentId) {
      caseType = encounterId ? 'DOCUMENT' : 'DOCUMENT_OUTSIDE_ENCOUNTER';
    } else {
      caseType = encounterType;
    }
    const dynamicItemDef: DynamicItemDefinition = {
      routeId: '',
      id: '',
      paramsString: '',
      label: '',
      cleanLocalStorage: [storageIdPrefix],
    };
    switch (caseType) {
      case 'HOSPITALIZATION':
      case 'OUTPATIENT':
      case 'DOCUMENT': {
        const isDocument = caseType === 'DOCUMENT';
        dynamicItemDef.isLoading = encounterIsLoading || (isDocument && documentIsLoading);
        dynamicItemDef.routeId = 'encounter';
        dynamicItemDef.id = encounterId;
        dynamicItemDef.paramsString = documentId ? `/${encounterId}/document/${documentId}` : `/${encounterId}`;
        dynamicItemDef.isPrefix = true;
        dynamicItemDef.iconClass = encounterType === 'OUTPATIENT' ? 'fas fa-stethoscope' : 'fas fa-bed';
        dynamicItemDef.label = [
          formatPatientBreadcrumb(breadcrumbProps.patient),
          formatEncounterBreadcrumb(breadcrumbProps.encounter, formatters),
          ...(isDocument ? [formatDocumentBreadcrumb(breadcrumbProps.document, formatters)] : []),
        ].join(' | ');
        dynamicItemDef.query = {docPaging: query.docPaging};
        break;
      }
      case 'DOCUMENT_OUTSIDE_ENCOUNTER':
        dynamicItemDef.isLoading = documentIsLoading;
        dynamicItemDef.routeId = 'document';
        dynamicItemDef.id = documentId;
        dynamicItemDef.paramsString = `/${documentId}`;
        dynamicItemDef.iconClass = 'fas fa-microscope';
        dynamicItemDef.label = [
          formatPatientBreadcrumb(breadcrumbProps.patient),
          formatDocumentBreadcrumb(breadcrumbProps.document, formatters),
        ].join(' | ');
        break;
    }
    addDynamicItem(dynamicItemDef);
  }, [
    addDynamicItem,
    encounterIsLoading,
    encounterId,
    documentIsLoading,
    documentId,
    query.docPaging,
    storageIdPrefix,
    encounterData,
    document,
    formatters,
    breadcrumbProps,
  ]);

  const isLoading = false;
  return (
    <div
      className={classNames({
        [stylesLayout.dlFullHeightColumn]: true,
        [stylesLayout.dlScrollableColumn]: true,
        [stylesLayout.dlFlexColumn]: true,
        [stylesLayout.dlRightMargin]: true,
      })}
      key={encounterId}
    >
      <div
        className={classNames('dlLoadingSpinnerContainer', 'dlFlexBoxContainerInContainer', {dlLoadingSpinnerParent: isLoading})}
        data-name={'loadingSpinnerParent'}
      >
        <LoadingSpinner isVisible={isLoading} text={I18N.LOADING.id} />
        <div
          className={classNames({
            [stylesLayout.dlFullHeightColumn]: true,
            [stylesLayout.dlTopMargin]: true,
            [stylesLayout.dlScrollableColumn]: true,
            [stylesLayout.dlFlexRow]: true,
          })}
        >
          <LightSplitLayout initSizes={[70, 30]} minSizes={[100, 56]} storageId={`${storageIdPrefix}.splitContentTabs`} gutterSize={18}>
            <div
              className={classNames({
                [stylesLayout.dlCenterColumn]: true,
              })}
            >
              <PatientEncounterDocumentBreadcrumbs
                patient={breadcrumbProps.patient}
                encounter={breadcrumbProps.encounter}
                document={breadcrumbProps.document}
              />
              {documentId ? (
                <EncounterDocumentLayout documentIsLoading={documentIsLoading} document={document} />
              ) : (
                <EncounterLayout
                  config={config}
                  encounterId={encounterId}
                  encounterData={encounterData}
                  encounterIsLoading={encounterIsLoading}
                  query={query}
                  changeQuery={changeQuery}
                />
              )}
            </div>
            <aside
              data-name='right-column'
              className={classNames({
                [stylesLayout.dlRightColumn]: true,
              })}
              style={{borderRadius: '15px'}}
            >
              <EncounterLayoutTabs
                config={config}
                encounterId={encounterId}
                documentId={documentId}
                encounterData={encounterData}
                onlyProperties={!encounterId} // NOTE: lab report page has only properties tab
                storageIdPrefix={storageIdPrefix}
                query={query}
                changeQuery={changeQuery}
              />
            </aside>
          </LightSplitLayout>
        </div>
      </div>
    </div>
  );
};

EncounterSharedLayout.displayName = 'EncounterSharedLayout';
